import {useFeatureFlags} from '@smart/feature-app';
import type {TextButtonProps} from '@smart/web-components';
import {TextButton, useOutsideClick} from '@smart/web-components';
import classNames from 'classnames';
import React, {useEffect, useRef} from 'react';
import {FocusOn} from 'react-focus-on';
import {UsedFeatureFlags} from '../../constants/common';
import styles from './index.module.scss';

type FilterNavigationButtonProps = Omit<
  TextButtonProps,
  'variant' | 'size' | 'isFullWidth'
>;

export type FilterNavigationProps = {
  isOpen: boolean;
  outsideClick: () => void;
  ariaDescription: string;
  dialogLabelledBy?: string;
  children: React.ReactNode;
  firstCta: FilterNavigationButtonProps;
  secondCta: FilterNavigationButtonProps;
} & Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>;

const noScrollClass = `no-scroll`;

export const FilterNavigation: React.FC<FilterNavigationProps> = ({
  isOpen,
  outsideClick,
  ariaDescription,
  dialogLabelledBy,
  className,
  children,
  firstCta,
  secondCta,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const classes = `${styles.filterDialog} ${
    (!isOpen && styles[`filterDialog--hidden`]) || ``
  } ${className || ``}`.trim();
  const {enabled: enableFilters} = useFeatureFlags(
    UsedFeatureFlags.ENABLE_STOCK_SEARCH_FILTER_V2,
  );
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add(noScrollClass);

      if (!ref.current || !scrollContainerRef.current) {
        return;
      }

      scrollContainerRef.current.scrollTop = 0;
    }

    return () => {
      document.body.classList.remove(noScrollClass);
    };
  }, [isOpen]);

  useOutsideClick(ref, outsideClick);
  const filterDialogWrapperClassName = classNames(styles.filterDialogWrapper, {
    [`${styles.filterDialogWrapperEnable}`]: enableFilters,
  });

  return (
    <>
      <FocusOn enabled={isOpen} onEscapeKey={outsideClick} autoFocus={false}>
        <div
          className={classes}
          aria-hidden={!isOpen}
          aria-modal={isOpen}
          aria-describedby={styles.filterDialogDescription}
          aria-labelledby={dialogLabelledBy || ``}
        >
          <div className={filterDialogWrapperClassName} ref={ref}>
            <div
              className={styles.filterDialogContent}
              ref={scrollContainerRef}
            >
              {children}
            </div>
            <div className={styles.filterDialogButtons}>
              <TextButton
                variant="primary"
                size="300"
                isFullWidth
                {...firstCta}
              />
              <TextButton
                variant="secondary"
                size="300"
                isFullWidth
                {...secondCta}
              />
            </div>
            <div
              className={styles.filterDialogDescription}
              id={styles.filterDialogDescription}
            >
              {ariaDescription}
            </div>
          </div>
        </div>
      </FocusOn>
      {isOpen && <div className={styles.filterDialogBackdrop} />}
    </>
  );
};
